import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  FaArrowLeft, FaSyncAlt, FaFileExcel, FaFilePdf, FaSchool, FaMapMarkedAlt, FaUsers, FaCamera, FaCertificate, FaBus,
  FaSearch, FaLock, FaChevronRight, FaSortUp, FaSortDown, FaSort, FaClipboardList, FaTable,
} from "react-icons/fa";
import axiosConn from "config/axiosConn";
import {
  QUESTIONS, BINS_OPTIONS, YES_NO, COMPOST_OPTIONS, AWARENESS_TYPES, COMPETITIONS, REDUCTION_ACTIVITIES,
} from "../Swachhata/swachhataSurvey";
import "./SwmReport.css";

/*
 * /admin/swm-report — the detailed Solid Waste Management report.
 *
 * One screen, top to bottom:
 *   1. Headline figures — schools that submitted, States/UTs (or districts)
 *      reporting, people reached, photos, certificates, exposure visits.
 *   2. Question-wise summary — every question of the school form (Q1–Q11):
 *      how many schools chose each option, and the share.
 *   3. State-wise (national) or district-wise (a state) table with the same
 *      measures per area, click-to-sort, search, totals row, click to drill.
 *   4. Excel and PDF downloads of exactly what is on screen.
 *
 * Central admin (role 1): India → State → District. State admin (role 4):
 * locked to its own state (the server enforces this too). Figures come from
 * POST swachhata/admin/swm-report, cached server-side for ~2 minutes.
 */

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("en-IN"));
const pct = (n, d) => (d ? Math.round(((n || 0) / d) * 1000) / 10 : 0);
const pctText = (n, d) => (d ? `${pct(n, d)}%` : "—");
const titleCase = (s) => String(s || "").toLowerCase().replace(/(^|[\s(&/-])([a-z])/g, (m, p, c) => p + c.toUpperCase());
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const isAuthFailure = (e) => e?.response?.status === 401 || /please login|session expired|json web token/i.test(e?.response?.data?.message || e?.message || "");
const post = async (url, payload) => {
  const res = await axiosConn("post", url, payload);
  if (!res?.data?.success) throw new Error(res?.data?.message || "Request failed");
  return res.data;
};
const labelOf = (list) => (key) => list.find((o) => o.key === key)?.label || titleCase(key);

const TONE = { green: "var(--green)", orange: "var(--orange)", red: "var(--red)", blue: "var(--blue)", teal: "var(--teal)", violet: "var(--violet)", slate: "var(--slate)" };
const SOFT = { green: "var(--green-soft)", orange: "var(--orange-soft)", red: "var(--red-soft)", blue: "var(--blue-soft)", teal: "var(--teal-soft)", violet: "var(--violet-soft)", slate: "var(--slate-soft)" };
const MULTI_TONES = ["green", "blue", "teal", "violet", "orange", "slate", "red"];

// ─── Question specification ──────────────────────────────────────────────────
// Turns the server's counts into the cards on screen and the rows in the
// exports. `base` is the denominator for the share column.
const yesNo = (yes, no) => [
  { key: "yes", label: "Yes", count: yes, tone: "green" },
  { key: "no", label: "No", count: no, tone: "red" },
];
const multi = (list, labels) => list.map((o, i) => ({ key: o.key, label: labelOf(labels)(o.key), count: o.count, tone: MULTI_TONES[i % MULTI_TONES.length] }));

function buildQuestions(d) {
  const s = d.summary;
  const L = d.lists;
  const n = s.submitted;
  const submittedNote = "of schools that submitted the report";
  return [
    { id: "Q1", text: QUESTIONS.q1, kind: "single", base: n, note: submittedNote,
      options: [
        { key: "all_four", label: labelOf(BINS_OPTIONS)("all_four"), count: s.binsAllFour, tone: "green" },
        { key: "not_installed", label: labelOf(BINS_OPTIONS)("not_installed"), count: s.binsNotInstalled, tone: "red" },
      ] },
    { id: "Q2", text: QUESTIONS.q2, kind: "single", base: n, note: submittedNote, options: yesNo(s.awarenessYes, s.awarenessNo) },
    { id: "Q2A", sub: true, text: QUESTIONS.q2a, kind: "multi", base: s.awarenessYes, note: "of schools that conducted an awareness activity · a school may pick more than one", options: multi(L.awarenessTypes, AWARENESS_TYPES) },
    { id: "Q3", text: QUESTIONS.q3, kind: "multi", base: n, note: `${submittedNote} · a school may pick more than one`, options: multi(L.competitions, COMPETITIONS) },
    { id: "Q4", text: QUESTIONS.q4, kind: "number", total: s.students, schools: s.schoolsWithStudents, unit: "students", base: n },
    { id: "Q5", text: QUESTIONS.q5, kind: "number", total: s.teachers, schools: s.schoolsWithTeachers, unit: "teachers", base: n },
    { id: "Q6", text: QUESTIONS.q6, kind: "number", total: s.community, schools: s.schoolsWithCommunity, unit: "community members", base: n },
    { id: "Q7", text: QUESTIONS.q7, kind: "single", base: n, note: submittedNote, options: yesNo(s.auditYes, s.auditNo) },
    { id: "Q8", text: QUESTIONS.q8, kind: "single", base: n, note: submittedNote, options: yesNo(s.reductionYes, s.reductionNo) },
    { id: "Q8A", sub: true, text: QUESTIONS.q8a, kind: "multi", base: s.reductionYes, note: "of schools that undertook a waste reduction activity · a school may pick more than one", options: multi(L.reductionActivities, REDUCTION_ACTIVITIES) },
    { id: "Q9", text: QUESTIONS.q9, kind: "single", base: n, note: submittedNote,
      options: [
        { key: "yes", label: "Yes", count: s.compostYes, tone: "green" },
        { key: "no", label: "No", count: s.compostNo, tone: "red" },
        { key: "na", label: labelOf(COMPOST_OPTIONS)("na"), count: s.compostNa, tone: "slate" },
      ] },
    { id: "Q10", text: QUESTIONS.q10, kind: "single", base: n, note: submittedNote,
      options: [
        { key: "two", label: "Uploaded 2 files", count: s.photosTwo, tone: "teal" },
        { key: "three", label: "Uploaded 3 files", count: s.photosThree, tone: "green" },
      ],
      facts: [["Total files uploaded", s.photos], ["PDF documents among them", s.pdfFiles]] },
    { id: "Q11", text: QUESTIONS.q11, kind: "single", wide: true, base: n, note: `${submittedNote} · reports filed before 21 Sep 2026 have no answer to this question`,
      options: [
        { key: "yes", label: "Yes", count: s.exposureYes, tone: "green" },
        { key: "no", label: "No", count: s.exposureNo, tone: "red" },
        { key: "na", label: "Not answered (filed before Q11 was added)", count: s.exposureNotAnswered, tone: "slate" },
      ],
      facts: [["Students on visits", s.visitStudents], ["Teachers on visits", s.visitTeachers], ["Geo-tagged visit photos", s.visitPhotos]] },
  ];
}

// Area (State / District) table columns. One list drives the table, the
// Excel sheet and the PDF so all three read the same.
const AREA_COLS = [
  { key: "submitted", label: "Reports submitted", short: "Reports" },
  { key: "binsAllFour", label: "All 4 bins installed", short: "4 bins", share: true },
  { key: "awarenessYes", label: "Awareness activity held", short: "Awareness", share: true },
  { key: "auditYes", label: "Waste audit done", short: "Audit", share: true },
  { key: "reductionYes", label: "Reduce / reuse / recycle", short: "3R activity", share: true },
  { key: "compostYes", label: "Composting wet waste", short: "Composting", share: true },
  { key: "exposureYes", label: "Exposure visit held", short: "Exposure visit", share: true },
  { key: "students", label: "Students participated", short: "Students" },
  { key: "teachers", label: "Teachers participated", short: "Teachers" },
  { key: "community", label: "Community members", short: "Community" },
  { key: "photos", label: "Photos / files", short: "Files" },
  { key: "certificates", label: "Certificates issued", short: "Certificates" },
];

// ─── Small building blocks ───────────────────────────────────────────────────
function Kpi({ icon, tone, label, value, sub, i }) {
  return (
    <div className="swr-kpi" style={{ "--tone": TONE[tone], "--tone-soft": SOFT[tone], "--i": i }}>
      <span className="swr-kpi__icon">{icon}</span>
      <span className="swr-kpi__label">{label}</span>
      <span className="swr-kpi__value">{value}</span>
      {sub ? <span className="swr-kpi__sub">{sub}</span> : null}
    </div>
  );
}

function OptionRow({ o, base }) {
  const share = pct(o.count, base);
  return (
    <div className="swr-opt" style={{ "--tone": TONE[o.tone] || TONE.green }}>
      <div className="swr-opt__label" title={o.label}><i /><span>{o.label}</span></div>
      <div className="swr-opt__track"><i style={{ "--w": `${Math.min(100, share)}%` }} /></div>
      <div className="swr-opt__count">{fmt(o.count)}</div>
      <div className="swr-opt__pct">{pctText(o.count, base)}</div>
    </div>
  );
}

function QuestionCard({ q, i }) {
  return (
    <div className={`swr-q${q.wide ? " swr-q--wide" : ""}`} style={{ "--i": i }}>
      <div className="swr-q__head">
        <span className={`swr-q__num${q.sub ? " is-sub" : ""}`}>{q.id}</span>
        <div>
          <div className="swr-q__text">{q.text}</div>
          <div className="swr-q__base">
            {q.kind === "number"
              ? `Totals as reported by ${fmt(q.base)} schools`
              : `Share ${q.note} (${fmt(q.base)})`}
          </div>
        </div>
      </div>
      <div className="swr-q__body">
        {q.kind === "number" ? (
          <div className="swr-nums">
            <div className="swr-num"><b>{fmt(q.total)}</b><span>{`Total ${q.unit}`}</span></div>
            <div className="swr-num"><b>{fmt(q.schools)}</b><span>{`Schools reporting 1 or more`}</span></div>
            <div className="swr-num"><b>{q.schools ? fmt(Math.round(q.total / q.schools)) : "—"}</b><span>{`Average per such school`}</span></div>
          </div>
        ) : (
          q.options.map((o) => <OptionRow key={o.key} o={o} base={q.base} />)
        )}
        {q.facts ? (
          <div className="swr-facts">
            {q.facts.map(([l, v]) => <span key={l} className="swr-fact">{l}<b>{fmt(v)}</b></span>)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ShareCell({ count, base }) {
  const share = pct(count, base);
  const cls = !count ? "is-zero" : share < 50 ? "is-low" : "";
  return (
    <>
      {fmt(count)}
      <span className={`swr-cell-pct ${cls}`}>{base ? `${share}%` : "—"}</span>
    </>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function SwmReport() {
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const isStateAdmin = user?.role === 4;

  const empty = { state: "", district: "", from: "", to: "" };
  const [draft, setDraft] = useState(empty);
  const [applied, setApplied] = useState(empty);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "submitted", dir: "desc" });
  const [exporting, setExporting] = useState("");

  const scopeOf = (f) => {
    const p = {};
    ["state", "district", "from", "to"].forEach((k) => { if (f[k]) p[k] = f[k]; });
    return p;
  };

  const load = useCallback(async (f) => {
    setLoading(true); setError(""); setExpired(false);
    try {
      const d = await post("swachhata/admin/swm-report", scopeOf(f));
      setData(d);
      if (!f.state && !d.scope?.locked) setStateOptions(d.rows.filter((r) => r.key));
      if (d.level === "district" && !f.district) setDistrictOptions(d.rows.filter((r) => r.key));
    } catch (e) {
      if (isAuthFailure(e)) setExpired(true);
      else setError(e?.response?.data?.message || e?.message || "The report could not be loaded — please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(applied); }, [applied, load]);

  const apply = (f) => { setDraft(f); setApplied(f); setQuery(""); };

  const level = data?.level || (applied.state ? "district" : "state");
  const lockedState = data?.scope?.locked ? data.scope.state : null;
  const areaWord = level === "district" ? "District" : "States/UTs/ABs";
  const areaWordPlural = level === "district" ? "districts" : "States/UTs/ABs";
  const scopeName = applied.district ? titleCase(applied.district) : lockedState || (applied.state ? titleCase(applied.state) : "All India");
  const crumbs = [
    !isStateAdmin && { label: "India", onClick: applied.state ? () => apply({ ...applied, state: "", district: "" }) : null },
    (applied.state || lockedState) && { label: lockedState || titleCase(applied.state), onClick: applied.district ? () => apply({ ...applied, district: "" }) : null },
    applied.district && { label: titleCase(applied.district), onClick: null },
  ].filter(Boolean);

  const s = data?.summary;
  const questions = useMemo(() => (data ? buildQuestions(data) : []), [data]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (data?.rows || []).filter((r) => !q || r.name.toLowerCase().includes(q));
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort.key === "name") return a.name.localeCompare(b.name) * dir;
      return ((a[sort.key] || 0) - (b[sort.key] || 0)) * dir || a.name.localeCompare(b.name);
    });
  }, [data, query, sort]);
  const totals = useMemo(() => {
    const t = {};
    AREA_COLS.forEach((c) => { t[c.key] = rows.reduce((acc, r) => acc + (r[c.key] || 0), 0); });
    return t;
  }, [rows]);

  const toggleSort = (key) => setSort((cur) => (cur.key === key ? { key, dir: cur.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" ? "asc" : "desc" }));
  const sortIcon = (key) => (sort.key !== key ? <FaSort size={10} style={{ opacity: 0.5 }} /> : sort.dir === "asc" ? <FaSortUp size={11} /> : <FaSortDown size={11} />);
  const drill = (row) => {
    if (!row.key) return;
    if (level === "state" && !isStateAdmin) apply({ ...applied, state: row.key, district: "" });
    else if (level === "district" && !applied.district) apply({ ...applied, district: row.key });
  };
  const canDrill = (level === "state" && !isStateAdmin) || (level === "district" && !applied.district);

  // ── exports ──
  const stamp = () => new Date().toISOString().slice(0, 10);
  const fileScope = () => [applied.state || lockedState, applied.district].filter(Boolean).map(titleCase).join("_").replace(/\s+/g, "_") || "India";
  const periodText = () => (applied.from || applied.to ? `Reports first submitted ${applied.from ? `from ${applied.from}` : ""}${applied.to ? ` to ${applied.to}` : ""}`.trim() : "All reports since the campaign began");
  const kpiRows = () => [
    ["Schools that submitted the report", s.submitted],
    [`${areaWord === "District" ? "Districts" : "States/UTs/ABs"} reporting`, s.areas],
    ["Reports updated after first submission", s.updated],
    ["Participation certificates issued", s.certificates],
    ["Students participated (Q4)", s.students],
    ["Teachers participated (Q5)", s.teachers],
    ["Community members participated (Q6)", s.community],
    ["Total people reached", s.participants],
    ["Photos / files uploaded (Q10)", s.photos],
    ["Schools that held an exposure visit (Q11)", s.exposureYes],
    ["Students on exposure visits", s.visitStudents],
    ["Teachers on exposure visits", s.visitTeachers],
  ];
  const questionRows = () => {
    const out = [];
    questions.forEach((q) => {
      if (q.kind === "number") {
        out.push([q.id, q.text, `Total ${q.unit}`, q.total]);
        out.push([q.id, q.text, "Schools reporting 1 or more", q.schools]);
        out.push([q.id, q.text, "Average per such school", q.schools ? Math.round(q.total / q.schools) : 0]);
      } else {
        q.options.forEach((o) => out.push([q.id, q.text, o.label, o.count]));
        (q.facts || []).forEach(([l, v]) => out.push([q.id, q.text, l, v]));
      }
    });
    return out;
  };

  const exportExcel = () => {
    if (!s) return;
    setExporting("excel");
    try {
      const wb = XLSX.utils.book_new();
      const title = `Solid Waste Management Report - Swachhata Hi Seva 2026`;
      const summary = [
        [title], [`Scope: ${scopeName}`], [periodText()], [`Generated: ${new Date().toLocaleString("en-IN")}`], [],
        ["Indicator", "Value"], ...kpiRows(),
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      ws1["!cols"] = [{ wch: 48 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws1, "Summary");

      const ws2 = XLSX.utils.aoa_to_sheet([["Question", "Question text", "Option / measure", "Schools / value"], ...questionRows()]);
      ws2["!cols"] = [{ wch: 8 }, { wch: 70 }, { wch: 44 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Question-wise");

      const head = ["#", areaWord];
      AREA_COLS.forEach((c) => { head.push(c.label); if (c.share) head.push(`${c.short} % of reports`); });
      const body = rows.map((r, i) => {
        const line = [i + 1, r.name];
        AREA_COLS.forEach((c) => { line.push(r[c.key] || 0); if (c.share) line.push(r.submitted ? pct(r[c.key], r.submitted) : 0); });
        return line;
      });
      const foot = ["", `Total (${rows.length} ${areaWordPlural})`];
      AREA_COLS.forEach((c) => { foot.push(totals[c.key]); if (c.share) foot.push(totals.submitted ? pct(totals[c.key], totals.submitted) : 0); });
      const ws3 = XLSX.utils.aoa_to_sheet([head, ...body, foot]);
      ws3["!cols"] = [{ wch: 5 }, { wch: 28 }, ...head.slice(2).map(() => ({ wch: 16 }))];
      XLSX.utils.book_append_sheet(wb, ws3, level === "district" ? "District-wise" : "States-UTs-ABs-wise");

      XLSX.writeFile(wb, `Swachhata_Hi_Seva_2026_${fileScope()}_${stamp()}.xlsx`);
    } finally {
      setExporting("");
    }
  };

  const exportPdf = () => {
    if (!s) return;
    setExporting("pdf");
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 36;
      const GREEN = [19, 138, 71];
      const INK = [15, 36, 24];

      // header band
      doc.setFillColor(13, 90, 47); doc.rect(0, 0, W, 78, "F");
      doc.setTextColor(255); doc.setFont("helvetica", "bold"); doc.setFontSize(17);
      doc.text("Swachhata Hi Seva", M, 32);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10.5);
      doc.text("Swachhata Hi Seva 2026 · Swachh Paathshala: Yuva for Swachhata · School report form (Q1–Q11)", M, 50);
      doc.setFontSize(9.5);
      doc.text(`Scope: ${scopeName}   ·   ${periodText()}   ·   Generated ${new Date().toLocaleString("en-IN")}`, M, 66);

      // KPI boxes
      const kpis = [
        ["Schools submitted", fmt(s.submitted)], [areaWord === "District" ? "Districts reporting" : "States/UTs/ABs reporting", fmt(s.areas)],
        ["People reached", fmt(s.participants)], ["Students", fmt(s.students)], ["Teachers", fmt(s.teachers)],
        ["Photos / files", fmt(s.photos)], ["Certificates", fmt(s.certificates)], ["Exposure visits", fmt(s.exposureYes)],
      ];
      const bw = (W - M * 2 - 7 * 8) / 8;
      kpis.forEach(([l, v], i) => {
        const x = M + i * (bw + 8);
        doc.setFillColor(243, 247, 244); doc.roundedRect(x, 92, bw, 50, 6, 6, "F");
        doc.setFillColor(...GREEN); doc.rect(x, 92, bw, 3, "F");
        doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.text(String(v), x + 10, 118);
        doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(95, 115, 104); doc.text(l, x + 10, 132);
      });

      // question-wise table
      doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text("Question-wise summary", M, 166);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(95, 115, 104);
      doc.text("Number of schools choosing each option; totals for the participation questions.", M, 179);
      const qBody = [];
      questions.forEach((q) => {
        const lines = q.kind === "number"
          ? [[`Total ${q.unit}`, fmt(q.total)], ["Schools reporting 1 or more", fmt(q.schools)], ["Average per such school", q.schools ? fmt(Math.round(q.total / q.schools)) : "—"]]
          : [...q.options.map((o) => [o.label, fmt(o.count)]), ...(q.facts || []).map(([l, v]) => [l, fmt(v)])];
        lines.forEach((ln, i) => qBody.push([i === 0 ? { content: q.id, rowSpan: lines.length, styles: { fontStyle: "bold", valign: "top", fillColor: [231, 245, 236], textColor: GREEN } } : null, i === 0 ? { content: q.text, rowSpan: lines.length, styles: { valign: "top" } } : null, ...ln].filter((c) => c !== null)));
      });
      doc.autoTable({
        startY: 188,
        head: [["Q", "Question", "Option / measure", "Schools"]],
        body: qBody,
        styles: { fontSize: 8, cellPadding: 4, lineColor: [225, 232, 228], lineWidth: 0.5, textColor: INK },
        headStyles: { fillColor: INK, textColor: 255, fontSize: 8 },
        columnStyles: { 0: { cellWidth: 34 }, 1: { cellWidth: 290 }, 2: { cellWidth: "auto" }, 3: { halign: "right", cellWidth: 90 } },
        alternateRowStyles: { fillColor: [250, 252, 250] },
        margin: { left: M, right: M, top: 40 },
      });

      // area table
      doc.addPage();
      doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(`${areaWord}-wise summary · ${scopeName}`, M, 40);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(95, 115, 104);
      doc.text("Counts are schools, except Students / Teachers / Community / Files. Percentages are of that area's submitted reports.", M, 53);
      const cellText = (r, c) => (c.share ? `${fmt(r[c.key])}  (${r.submitted ? `${pct(r[c.key], r.submitted)}%` : "—"})` : fmt(r[c.key]));
      doc.autoTable({
        startY: 62,
        head: [["#", areaWord, ...AREA_COLS.map((c) => c.label)]],
        body: rows.map((r, i) => [i + 1, r.name, ...AREA_COLS.map((c) => cellText(r, c))]),
        foot: [["", `Total · ${rows.length} ${areaWordPlural}`, ...AREA_COLS.map((c) => cellText({ ...totals }, c))]],
        styles: { fontSize: 7.5, cellPadding: 3.5, lineColor: [225, 232, 228], lineWidth: 0.5, textColor: INK },
        headStyles: { fillColor: GREEN, textColor: 255, fontSize: 7.2, valign: "middle" },
        footStyles: { fillColor: [238, 245, 240], textColor: INK, fontStyle: "bold" },
        columnStyles: Object.fromEntries(AREA_COLS.map((_, i) => [i + 2, { halign: "right" }])),
        alternateRowStyles: { fillColor: [250, 252, 250] },
        margin: { left: M, right: M, top: 40 },
      });

      // footer on every page
      const pages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        doc.setFontSize(8); doc.setTextColor(120, 135, 126);
        doc.text("Eco Clubs for Mission LiFE · Swachhata Hi Seva 2026 · Solid Waste Management report", M, H - 18);
        doc.text(`Page ${p} of ${pages}`, W - M, H - 18, { align: "right" });
      }
      doc.save(`Swachhata_Hi_Seva_2026_${fileScope()}_${stamp()}.pdf`);
    } finally {
      setExporting("");
    }
  };

  // ── render ──
  return (
    <div className="swr">
      <section className="swr-top">
        <div className="swr-wrap">
          <div className="swr-top__row">
            <div>
              <span className="swr-kicker"><i />Swachhata Hi Seva 2026 · School report form</span>
              <div className="swr-title">Solid Waste Management Report</div>
              <div className="swr-sub">{`Every question of the school report, counted for ${scopeName}`}</div>
              <div className="swr-crumbs">
                {crumbs.map((c, i) => (
                  <React.Fragment key={c.label}>
                    {i > 0 && <FaChevronRight size={10} />}
                    {c.onClick ? <button type="button" onClick={c.onClick}>{c.label}</button> : <span>{c.label}</span>}
                  </React.Fragment>
                ))}
                {isStateAdmin && <span className="swr-lock"><FaLock size={11} />State admin view</span>}
              </div>
            </div>
            <div className="swr-actions">
              <button type="button" className="swr-btn swr-btn--glass" onClick={() => navigate("/admin/environment-day-dashboard")}><FaArrowLeft />Back to Reports</button>
              <button type="button" className="swr-btn swr-btn--glass" onClick={() => navigate("/admin/swachhata-hi-seva")}>Swachhata dashboard</button>
              <button type="button" className="swr-btn swr-btn--glass" onClick={() => load(applied)} disabled={loading}><FaSyncAlt />{loading ? "Refreshing…" : "Refresh"}</button>
              <button type="button" className="swr-btn swr-btn--excel" onClick={exportExcel} disabled={!s || loading || !!exporting}><FaFileExcel />{exporting === "excel" ? "Preparing…" : "Download Excel"}</button>
              <button type="button" className="swr-btn swr-btn--pdf" onClick={exportPdf} disabled={!s || loading || !!exporting}><FaFilePdf />{exporting === "pdf" ? "Preparing…" : "Download PDF"}</button>
            </div>
          </div>
        </div>
      </section>

      <div className="swr-wrap">
        {expired ? (
          <div className="swr-expired">
            <b>Your session has expired</b>
            <p>Please sign in again to open the Solid Waste Management report.</p>
            <button type="button" className="swr-btn swr-btn--primary" onClick={() => navigate("/admin")}>Go to login</button>
          </div>
        ) : (
          <>
            <div className="swr-filters">
              {!isStateAdmin && (
                <label className="swr-field">
                  <span>States/UTs/ABs</span>
                  <select value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value, district: "" })}>
                    <option value="">All States/UTs/ABs</option>
                    {stateOptions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    {draft.state && !stateOptions.some((r) => r.key === draft.state) && <option value={draft.state}>{titleCase(draft.state)}</option>}
                  </select>
                </label>
              )}
              {(applied.state || isStateAdmin) && (
                <label className="swr-field">
                  <span>District</span>
                  <select value={draft.district} onChange={(e) => setDraft({ ...draft, district: e.target.value })}>
                    <option value="">All districts</option>
                    {districtOptions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                    {draft.district && !districtOptions.some((r) => r.key === draft.district) && <option value={draft.district}>{titleCase(draft.district)}</option>}
                  </select>
                </label>
              )}
              <label className="swr-field"><span>Submitted from</span><input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
              <label className="swr-field"><span>Submitted to</span><input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
              <div className="swr-filters__btns">
                <button type="button" className="swr-btn swr-btn--primary" onClick={() => apply(draft)}>Apply</button>
                <button type="button" className="swr-btn swr-btn--soft" onClick={() => apply(empty)}>Reset</button>
              </div>
            </div>

            {error ? <div className="swr-note swr-note--error">{error}</div> : null}

            {/* ═══ 1. Headline figures ═══ */}
            <div className="swr-kpis">
              {loading && !s ? Array.from({ length: 6 }, (_, i) => <div key={i} className="swr-skel swr-skel--kpi" />) : s ? (
                <>
                  <Kpi i={0} icon={<FaSchool />} tone="green" label="Schools submitted" value={fmt(s.submitted)} sub={`${fmt(s.updated)} updated their report later`} />
                  <Kpi i={1} icon={<FaMapMarkedAlt />} tone="blue" label={areaWord === "District" ? "Districts reporting" : "States/UTs/ABs reporting"} value={fmt(s.areas)} sub={applied.district ? "Single district view" : `with at least one report`} />
                  <Kpi i={2} icon={<FaUsers />} tone="violet" label="People reached" value={fmt(s.participants)} sub={`${fmt(s.students)} students · ${fmt(s.teachers)} teachers · ${fmt(s.community)} community`} />
                  <Kpi i={3} icon={<FaCamera />} tone="teal" label="Photos / files uploaded" value={fmt(s.photos)} sub={`${fmt(s.pdfFiles)} are PDF documents`} />
                  <Kpi i={4} icon={<FaCertificate />} tone="orange" label="Certificates issued" value={fmt(s.certificates)} sub="one per school on first submission" />
                  <Kpi i={5} icon={<FaBus />} tone="red" label="Exposure visits held" value={fmt(s.exposureYes)} sub={`${fmt(s.visitStudents)} students · ${fmt(s.visitTeachers)} teachers on visits`} />
                </>
              ) : null}
            </div>

            {/* ═══ 2. Question-wise ═══ */}
            <section className="swr-section">
              <div className="swr-section__head">
                <div>
                  <div className="swr-section__title"><i />Question-wise summary</div>
                  <div className="swr-section__desc">How the schools answered each question of the report form. Bars show the share of the base named under each question.</div>
                </div>
                <span className="swr-fact"><FaClipboardList />{`${fmt(s?.submitted)} reports`}</span>
              </div>
              <div className="swr-questions">
                {loading && !s ? Array.from({ length: 6 }, (_, i) => <div key={i} className="swr-skel swr-skel--q" />) : questions.map((q, i) => <QuestionCard key={q.id} q={q} i={i} />)}
              </div>
            </section>

            {/* ═══ 3. Area table ═══ */}
            <section className="swr-section">
              <div className="swr-section__head">
                <div>
                  <div className="swr-section__title"><i className="is-blue" />{`${areaWord}-wise summary`}</div>
                  <div className="swr-section__desc">
                    {canDrill ? `Click a ${areaWord.toLowerCase()} to open its report. ` : ""}
                    Click a column heading to sort. Percentages are of that area&apos;s submitted reports.
                  </div>
                </div>
                <span className="swr-fact"><FaTable />{`${fmt(rows.length)} ${areaWordPlural}`}</span>
              </div>
              <div className="swr-tablecard">
                <div className="swr-toolbar">
                  <div className="swr-search"><FaSearch size={13} /><input type="search" placeholder={`Search ${areaWord}…`} value={query} onChange={(e) => setQuery(e.target.value)} /></div>
                  <span className="swr-toolbar__hint">{`Sorted by ${sort.key === "name" ? areaWord : AREA_COLS.find((c) => c.key === sort.key)?.label || sort.key} · ${sort.dir === "asc" ? "ascending" : "descending"}`}</span>
                </div>
                {loading && !s ? <div className="swr-skel swr-skel--table" style={{ borderRadius: 0 }} /> : rows.length === 0 ? (
                  <div className="swr-empty">No reports match these filters.</div>
                ) : (
                  <div className="swr-table-wrap">
                    <table className="swr-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th className={`is-sort ${sort.key === "name" ? "is-active" : ""}`} onClick={() => toggleSort("name")}><span className="swr-th">{areaWord}{sortIcon("name")}</span></th>
                          {AREA_COLS.map((c) => (
                            <th key={c.key} className={`is-sort ${sort.key === c.key ? "is-active" : ""}`} onClick={() => toggleSort(c.key)} title={c.label}>
                              <span className="swr-th">{c.label}{c.share ? <small>(%)</small> : null}{sortIcon(c.key)}</span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.key || r.name} className={canDrill && r.key ? "is-link" : ""} onClick={() => canDrill && drill(r)} title={canDrill && r.key ? `Open ${r.name}` : undefined}>
                            <td>{i + 1}</td>
                            <td>{r.name}</td>
                            {AREA_COLS.map((c) => (
                              <td key={c.key}>{c.share ? <ShareCell count={r[c.key]} base={r.submitted} /> : fmt(r[c.key])}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td />
                          <td>{`Total · ${fmt(rows.length)} ${areaWordPlural}`}</td>
                          {AREA_COLS.map((c) => (
                            <td key={c.key}>{c.share ? <ShareCell count={totals[c.key]} base={totals.submitted} /> : fmt(totals[c.key])}</td>
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </section>

            <div className="swr-foot">
              {`Source: Solid Waste Management report forms submitted by schools on the Eco Clubs portal · figures refresh every ~2 minutes${data?.generatedAt ? ` · last built ${fmtDate(data.generatedAt)}` : ""}`}
              <br />
              {"Q2A and Q8A are asked only of schools that answered “Yes” to Q2 / Q8. Q11 was added on 21 Sep 2026; earlier reports show as “Not answered” until the school updates its report."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
